import type { Enums, Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from './session.tsx';
import { supabase } from './supabase.ts';

/**
 * Regras de limiar por paciente (build-prompt §4.3).
 *
 * Escrita direta na tabela, sem RPC: `alert_rules` é uma das poucas tabelas com
 * INSERT/UPDATE/DELETE liberados por RLS para o médico, sempre condicionados a
 * `is_my_patient`. O INSERT exige ainda `created_by = current_doctor_id()` — se
 * esse campo não for preenchido com o id da linha em `doctors`, a policy recusa
 * sem dizer o porquê.
 *
 * AVISO QUE A UI PRECISA CARREGAR: nada avalia estas regras hoje. Não existe
 * função, trigger nem cron lendo `alert_rules` — o motor é a Fase 5. Uma regra
 * salva agora tem efeito zero, e num produto de monitoramento clínico deixar
 * isso implícito é criar falsa sensação de segurança.
 */

export type AlertRule = Tables<'alert_rules'>;
export type RuleOperator = Enums<'rule_operator'>;
export type MetricAggregation = Enums<'metric_aggregation'>;
export type TrafficLightStatus = Enums<'traffic_light_status'>;

/**
 * Símbolos dos operadores.
 *
 * Espelham exatamente o `case` dentro da view `doctor_patient_triage`, que monta
 * o motivo mostrado na lista de triagem. Se divergirem, a mesma regra apareceria
 * escrita de duas formas diferentes em duas telas do mesmo painel.
 */
export const OPERATOR_SYMBOL: Record<RuleOperator, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  neq: '≠',
};

export const AGGREGATION_LABEL: Record<MetricAggregation, string> = {
  latest: 'último valor',
  avg: 'média',
  min: 'mínimo',
  max: 'máximo',
  sum: 'soma',
};

/** Verde é rejeitado pelo banco: é a ausência de regra disparada, não um resultado. */
export const RULE_STATUSES: Exclude<TrafficLightStatus, 'green'>[] = ['yellow', 'red'];

export interface AlertRuleInput {
  metric_key: string;
  operator: RuleOperator;
  threshold_value: number;
  aggregation: MetricAggregation | null;
  window_minutes: number | null;
  resulting_status: Exclude<TrafficLightStatus, 'green'>;
  suppression_minutes: number;
  active: boolean;
}

export function useAlertRules(patientId: string) {
  return useQuery({
    queryKey: ['alert-rules', patientId],
    queryFn: async (): Promise<AlertRule[]> => {
      const { data, error } = await supabase
        .from('alert_rules')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateAlertRule(patientId: string) {
  const queryClient = useQueryClient();
  const { doctor } = useSession();

  return useMutation({
    mutationFn: async (input: AlertRuleInput) => {
      if (!doctor) throw new Error('Sessão de médico ausente');

      const { error } = await supabase.from('alert_rules').insert({
        ...input,
        patient_id: patientId,
        created_by: doctor.id,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules', patientId] }),
  });
}

export function useUpdateAlertRule(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<AlertRuleInput> }) => {
      const { error } = await supabase.from('alert_rules').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules', patientId] }),
  });
}

/**
 * Remove uma regra — quando ela ainda não virou evidência.
 *
 * Uma regra que já disparou é referenciada pelo histórico de estado e pelos
 * alertas gerados, e essas FKs são `on delete restrict` de propósito: apagar a
 * regra apagaria a resposta para "qual limiar causou este alerta?". O banco
 * recusa com 23503, e aqui isso vira uma frase que diz o que fazer, em vez de
 * um código de erro que só assusta.
 */
export function useDeleteAlertRule(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('alert_rules').delete().eq('id', id);
      if (error) {
        if (error.code === '23503') {
          throw new Error(
            'Esta regra já disparou alertas e por isso não pode ser apagada — o histórico ' +
              'do paciente depende dela para explicar o que aconteceu. Desative-a: ela para ' +
              'de ser avaliada e o histórico continua legível.',
          );
        }
        throw error;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules', patientId] }),
  });
}

/**
 * Copia regras de outro paciente para este (§4.3, escopo MVP §9).
 *
 * Existe porque o MVP não tem templates de condição: sem isso, um médico com
 * dez pacientes de perfil parecido repetiria a mesma configuração dez vezes na
 * mão. Acrescenta, nunca substitui — as regras já existentes ficam onde estão,
 * e a tela mostra antes o que será criado.
 */
export function useCopyAlertRules(patientId: string) {
  const queryClient = useQueryClient();
  const { doctor } = useSession();

  return useMutation({
    mutationFn: async (rules: AlertRule[]) => {
      if (!doctor) throw new Error('Sessão de médico ausente');
      if (rules.length === 0) throw new Error('Nenhuma regra para copiar');

      const { error } = await supabase.from('alert_rules').insert(
        rules.map((r) => ({
          patient_id: patientId,
          created_by: doctor.id,
          metric_key: r.metric_key,
          operator: r.operator,
          threshold_value: r.threshold_value,
          aggregation: r.aggregation,
          window_minutes: r.window_minutes,
          resulting_status: r.resulting_status,
          suppression_minutes: r.suppression_minutes,
          active: r.active,
        })),
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-rules', patientId] }),
  });
}

/** Regras de um paciente de ORIGEM, para a pré-visualização da cópia. */
export function useAlertRulesOf(sourcePatientId: string | null) {
  return useQuery({
    queryKey: ['alert-rules', sourcePatientId],
    enabled: sourcePatientId !== null,
    queryFn: async (): Promise<AlertRule[]> => {
      const { data, error } = await supabase
        .from('alert_rules')
        .select('*')
        .eq('patient_id', sourcePatientId!)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Duas regras são a mesma configuração?
 *
 * Compara o que define o comportamento da regra, ignorando id, autor e datas.
 * Serve para avisar na pré-visualização que uma cópia seria redundante — o banco
 * não tem unique constraint aqui, então nada impede duplicar; o que evita a
 * duplicata é o médico ver antes.
 */
export function sameRule(a: AlertRule, b: AlertRule): boolean {
  return (
    a.metric_key === b.metric_key &&
    a.operator === b.operator &&
    Number(a.threshold_value) === Number(b.threshold_value) &&
    a.aggregation === b.aggregation &&
    a.window_minutes === b.window_minutes &&
    a.resulting_status === b.resulting_status
  );
}
